import classNames from 'classnames';
import React, { ComponentPropsWithoutRef, forwardRef, useMemo } from 'react';
import classes from './styles/index.module.scss';

type HeadType = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

const headingSizeTagMap = {
	6: 'h6',
	5: 'h5',
	4: 'h4',
	3: 'h3',
	2: 'h2',
	1: 'h1',
};

type TypographyProps = {
	bold?: boolean;
	italic?: boolean;
	noWrap?: boolean;
	uppercase?: boolean;
	paragraph?: boolean;
	underline?: boolean;
	truncated?: boolean;
	dataTestId?: boolean;
	fontSize?: number | string;
	lineHeight?: number | string;
	color?: string;
	className?: string;
	headingSize?: 1 | 2 | 3 | 4 | 5 | 6;
	align?: 'left' | 'right' | 'center' | 'justify' | 'inherit';
	display?: 'block' | 'inline';
	title?: string;
	verticalAlign?: 'top' | 'bottom' | 'middle' | 'baseline' | 'sub' | 'super' | 'text-bottom' | 'text-top';
	lineClamp?: 2 | 3 | 4 | 5 | 6;
	whiteSpace?: string;
};

const Typography = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'span' | 'p' | HeadType> & TypographyProps>(
	(
		{
			bold,
			align,
			color,
			italic,
			noWrap,
			display,
			children,
			fontSize,
			uppercase,
			className,
			paragraph,
			underline,
			truncated,
			lineHeight,
			dataTestId,
			headingSize,
			title,
			verticalAlign,
			lineClamp,
			whiteSpace,
		},
		ref,
	) => {
		const TagName = useMemo(() => {
			if (headingSize) {
				return headingSizeTagMap[headingSize];
			}

			if (paragraph) {
				return 'p';
			}

			return 'span';
		}, [paragraph, headingSize]);

		const componentClasses = classNames(
			classes.typography,
			{
				[classes[`typography_h${headingSize}`]]: headingSize,
				[classes[`typography_verticalAlign-${verticalAlign}`]]: verticalAlign,
				[classes.typography_bold]: bold,
				[classes.typography_noWrap]: noWrap,
				[classes.typography_italic]: italic,
				[classes.typography_uppercase]: uppercase,
				[classes.typography_underline]: underline,
				[classes.typography_truncated]: truncated,
				[classes.typography_paragraph]: paragraph,

				[classes.typography_alignLeft]: align === 'left',
				[classes.typography_alignRight]: align === 'right',
				[classes.typography_alignCenter]: align === 'center',
				[classes.typography_alignJustify]: align === 'justify',

				[classes.typography_displayBlock]: display === 'block',
				[classes.typography_displayInline]: display === 'inline',
				[classes[`typography_lineClamp-${lineClamp}`]]: lineClamp,
			},
			className,
		);

		const style = useMemo(
			() => ({
				color,
				fontSize,
				lineHeight,
				whiteSpace,
			}),
			[color, fontSize, lineHeight, whiteSpace],
		);

		return (
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			<TagName
				style={style}
				className={componentClasses}
				data-testid={dataTestId}
				ref={ref}
				title={title}
			>
				{children}
			</TagName>
		);
	},
);

Typography.displayName = 'Typography';

export { Typography };
